import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  pushSupported,
  notificationPermission,
  getExistingPushSubscription,
  subscribeUser,
  unsubscribeUser,
  sendTestPush,
} from '../lib/pushClient';

/**
 * A compact card that lets a user turn on push notifications for this device.
 * Handles: unsupported browsers, "denied" permission, and existing subscriptions.
 * Also exposes a "Send test" button so users can verify delivery immediately.
 */
export const NotificationsToggle = () => {
  const [state, setState] = useState('loading'); // 'unsupported' | 'denied' | 'off' | 'on' | 'loading'
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!pushSupported()) return setState('unsupported');
    const perm = notificationPermission();
    if (perm === 'denied') return setState('denied');
    const sub = await getExistingPushSubscription();
    setState(sub ? 'on' : 'off');
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeUser();
      toast.success('Notifications enabled on this device');
      setState('on');
    } catch (e) {
      toast.error(e.message || 'Could not enable notifications');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeUser();
      toast.success('Notifications disabled on this device');
      setState('off');
    } catch (e) {
      toast.error(e.message || 'Could not disable notifications');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      const delivered = Number(res?.delivered) || 0;
      const subs = Number(res?.subscriptions) || 0;
      const pruned = Number(res?.pruned) || 0;

      if (delivered > 0) {
        // Long, actionable toast (sonner supports description). We CAN'T tell
        // from server-side alone whether iOS is going to actually show the
        // banner — Apple's push API returns 201 as soon as it queues the
        // message. If the phone is in Focus/DND, iOS silently routes the
        // push to Notification Center. This copy tells the user exactly
        // where to look and what to check.
        toast.success('Sent to Apple/Google push service', {
          description: 'On iPhone: pull down Notification Center to see it. If nothing shows there, turn Focus / Do Not Disturb OFF, then tap Send test again.',
          duration: 8000,
        });
      } else if (subs === 0) {
        toast.error('No devices are subscribed yet. Tap "Turn on" first, then Send test again.');
      } else if (pruned === subs) {
        // Every subscription came back 404/410 → invalid tokens.
        toast.error('Your device token expired and was cleared. Tap "Turn on" to resubscribe, then Send test again.');
      } else {
        toast.error(`Push provider rejected the request for ${subs - delivered} of ${subs} device(s). Check backend logs.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send test');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'unsupported') {
    return (
      <div className="p-4 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] flex items-start gap-3" data-testid="notifications-unsupported">
        <BellOff className="h-5 w-5 text-[#94a3b8] mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-[#334155]">Notifications not available</p>
          <p className="text-xs text-[#64748b] mt-1">
            Install My SPAN to your home screen (iOS 16.4+ or any modern Android) to receive parcel, announcement and maintenance alerts.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'denied') {
    return (
      <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="notifications-denied">
        <BellOff className="h-5 w-5 text-amber-600 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-900">Notifications are blocked in your browser</p>
          <p className="text-xs text-amber-800 mt-1">
            Open your browser settings → Site permissions → Notifications, then allow My SPAN and reload the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-4 rounded-lg border flex items-start gap-3 ${
        state === 'on' ? 'border-[#bfdbfe] bg-blue-50' : 'border-[#e2e8f0] bg-white'
      }`}
      data-testid="notifications-toggle"
    >
      <div className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 ${state === 'on' ? 'bg-[#2563eb] text-white' : 'bg-[#f1f5f9] text-[#64748b]'}`}>
        {state === 'on' ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>
          Push Notifications
        </p>
        <p className="text-xs text-[#64748b] mt-0.5">
          {state === 'on'
            ? 'You will get instant alerts for parcels, announcements, and maintenance updates on this device.'
            : 'Get instant alerts on this device for new parcels, announcements, and maintenance updates.'}
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {state === 'on' ? (
            <>
              <Button
                onClick={disable}
                disabled={busy || state === 'loading'}
                size="sm"
                variant="outline"
                className="border-[#e2e8f0] h-8"
                data-testid="disable-notifications-button"
              >
                <BellOff className="h-3.5 w-3.5 mr-1.5" />
                Turn off
              </Button>
              <Button
                onClick={test}
                disabled={busy}
                size="sm"
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-8"
                data-testid="test-notification-button"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Send test
              </Button>
            </>
          ) : (
            <Button
              onClick={enable}
              disabled={busy || state === 'loading'}
              size="sm"
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-8"
              data-testid="enable-notifications-button"
            >
              <Bell className="h-3.5 w-3.5 mr-1.5" />
              {busy ? 'Enabling…' : 'Turn on notifications'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsToggle;
